
import { Component, Vue, Watch } from 'vue-property-decorator'
import { getUserPage, updateUserRemark, IUser } from '@/api/user'
import { UserModule } from '@/store/modules/user'
import UserNameRemark from '@/components/UserNameRemark/index.vue'
import { scheduleElTableFixedSync } from '@/utils/elTableFixedSync'

@Component({
  name: 'UserManage',
  components: { UserNameRemark },
})
export default class extends Vue {
  private loading: boolean = false
  private list: IUser[] = []
  private total = 0

  private query = {
    pageNum: 1,
    pageSize: 10,
    name: '',
    phone: '',
    inviterPhone: '',
    orderBy: 'emptyBucketOutstanding',
    sortOrder: 'desc'
  }

  private detailVisible = false
  private currentUser: IUser | null = null

  private remarkVisible = false
  private remarkSaving = false
  private remarkUser: IUser | null = null
  private remarkText = ''

  get currentStoreId() {
    const sid = UserModule.storeId
    if (!sid) return null
    const n = Number(sid)
    return Number.isNaN(n) ? null : n
  }

  @Watch('currentStoreId')
  private onStoreChange() {
    this.query.pageNum = 1
    this.loadList()
  }

  mounted() {
    this.loadList()
    window.addEventListener('resize', this.scheduleTableLayout)
  }

  beforeDestroy() {
    window.removeEventListener('resize', this.scheduleTableLayout)
  }

  private scheduleTableLayout() {
    scheduleElTableFixedSync(this, 'mainTable')
  }

  private async loadList() {
    if (this.currentStoreId == null) {
      // 无当前店铺时故意不发 /user/page（按店隔离）
      this.list = []
      this.total = 0
      return
    }
    this.loading = true
    try {
      const params: any = {
        pageNum: this.query.pageNum,
        pageSize: this.query.pageSize,
        storeId: this.currentStoreId
      }
      if (this.query.name) params.name = this.query.name.trim()
      if (this.query.phone) params.phone = this.query.phone.trim()
      if (this.query.inviterPhone) params.inviterPhone = this.query.inviterPhone.trim()
      params.orderBy = this.query.orderBy || 'emptyBucketOutstanding'
      params.sortOrder = this.query.sortOrder || 'desc'
      const res: any = await getUserPage(params)
      if (String(res.data.code) === '1' && res.data.data) {
        this.list = res.data.data.records || []
        this.total = res.data.data.total || 0
      } else {
        this.list = []
        this.total = 0
        if (res && res.data && res.data.msg) {
          this.$message.error(res.data.msg)
        }
      }
    } catch (e) {
      this.list = []
      this.total = 0
      this.$message.error('加载用户失败：' + ((e as Error).message || '网络错误'))
    } finally {
      this.loading = false
      this.scheduleTableLayout()
    }
  }

  private tableIndex(index: number) {
    return (this.query.pageNum - 1) * this.query.pageSize + index + 1
  }

  private handleSearch() {
    this.query.pageNum = 1
    this.loadList()
  }

  private handleReset() {
    this.query.pageNum = 1
    this.query.pageSize = 10
    this.query.name = ''
    this.query.phone = ''
    this.query.inviterPhone = ''
    this.query.orderBy = 'emptyBucketOutstanding'
    this.query.sortOrder = 'desc'
    this.loadList()
  }

  private handleSortChange(payload: { prop?: string; order?: string | null }) {
    const { prop, order } = payload || {}
    if (prop === 'emptyBucketOutstanding' && order) {
      this.query.orderBy = 'emptyBucketOutstanding'
      this.query.sortOrder = order === 'ascending' ? 'asc' : 'desc'
    } else if (prop === 'createTime' && order) {
      this.query.orderBy = 'createTime'
      this.query.sortOrder = order === 'ascending' ? 'asc' : 'desc'
    } else {
      this.query.orderBy = 'emptyBucketOutstanding'
      this.query.sortOrder = 'desc'
    }
    this.query.pageNum = 1
    this.loadList()
  }

  private handleSizeChange(size: number) {
    this.query.pageSize = size
    this.query.pageNum = 1
    this.loadList()
  }

  private handlePageChange(page: number) {
    this.query.pageNum = page
    this.loadList()
  }

  private openDetail(row: IUser) {
    this.currentUser = row
    this.detailVisible = true
  }

  private onDetailClosed() {
    this.currentUser = null
  }

  private openRemark(row: IUser) {
    this.remarkUser = row
    this.remarkText = row.remark || ''
    this.remarkVisible = true
  }

  private onRemarkClosed() {
    this.remarkUser = null
    this.remarkText = ''
    this.remarkSaving = false
  }

  private async saveRemark() {
    if (!this.remarkUser || this.currentStoreId == null) {
      this.$message.warning('请先选择商户')
      return
    }
    this.remarkSaving = true
    try {
      const res: any = await updateUserRemark(this.remarkUser.id, {
        storeId: this.currentStoreId,
        remark: (this.remarkText || '').trim()
      })
      if (String(res.data.code) === '1') {
        const text = (this.remarkText || '').trim()
        this.$set(this.remarkUser, 'remark', text || undefined)
        if (this.currentUser && this.currentUser.id === this.remarkUser.id) {
          this.$set(this.currentUser, 'remark', text || undefined)
        }
        this.$message.success('备注已保存')
        this.remarkVisible = false
      } else {
        this.$message.error((res.data && res.data.msg) || '保存失败')
      }
    } catch (e) {
      this.$message.error('保存失败')
    } finally {
      this.remarkSaving = false
    }
  }

  private otherAddressCount(row: IUser | null) {
    if (!row || !Array.isArray(row.otherAddresses)) return 0
    return row.otherAddresses.length
  }

  private outstandingCount(row: IUser | null) {
    const n = Number(row && row.emptyBucketOutstanding)
    return Number.isFinite(n) ? n : 0
  }

  private formatOutstanding(row: IUser | null) {
    const n = this.outstandingCount(row)
    if (n > 0) return `欠 ${n} 个`
    if (n < 0) return `多收 ${Math.abs(n)} 个`
    return '0 个'
  }

  private formatMoney(v: any) {
    const n = Number(v)
    return Number.isFinite(n) ? n.toFixed(2) : '0.00'
  }

  private avatarLetter(row: IUser | null) {
    const name = String((row && row.name) || '').trim()
    if (name) return name.slice(0, 1)
    const phone = String((row && row.phone) || '').trim()
    if (phone) return phone.slice(-2)
    return '用'
  }
}
